import { type FC, useState } from 'react'
import { Dropdown, Form, InputGroup } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { PortalDropdownMenu } from './PortalDropdownMenu'
import { BRAND_COLOR_PRESETS, brandColorName } from './schedulingDisplay'
import styles from './BrandColorSelect.module.scss'

interface BrandColorSelectProps {
  value: string
  readOnly?: boolean
  onChange: (hex: string) => void
}

const HEX_PATTERN = /^[0-9a-fA-F]{6}$/

const Swatch: FC<{ hex: string }> = ({ hex }) => (
  <span className={classNames(styles.swatch, 'd-inline-block rounded-1 border zocks-border-color-gray-300')} style={{ backgroundColor: hex }} />
)

export const BrandColorSelect: FC<BrandColorSelectProps> = ({ value, readOnly = false, onChange }) => {
  const [show, setShow] = useState(false)
  const [customHex, setCustomHex] = useState(() => (brandColorName(value) === 'Custom' ? value.replace('#', '') : ''))
  const [lastValue, setLastValue] = useState(value)
  if (lastValue !== value) {
    setLastValue(value)
    setCustomHex(brandColorName(value) === 'Custom' ? value.replace('#', '') : '')
  }

  if (readOnly) {
    return (
      <span className='d-flex align-items-center zocks-gap-0.25 paragraph-small zocks-text-gray-700'>
        <Swatch hex={value} />
        {value.toUpperCase()}
      </span>
    )
  }

  const isCustomHexValid = HEX_PATTERN.test(customHex)

  const commitCustomHex = () => {
    if (isCustomHexValid) {
      onChange(`#${customHex.toUpperCase()}`)
    }
  }

  return (
    <Dropdown show={show} autoClose='outside' onToggle={(nextShow) => setShow(nextShow)}>
      <Dropdown.Toggle as={Button} variant='grayGhost' className='d-flex align-items-center zocks-gap-0.5 border zocks-border-color-gray-300'>
        <Swatch hex={value} />
        <span className='paragraph-small'>
          {brandColorName(value)} ({value.toUpperCase()})
        </span>
        <i className='bi bi-chevron-expand zocks-text-gray-600' />
      </Dropdown.Toggle>
      <Dropdown.Menu as={PortalDropdownMenu} popperConfig={{ strategy: 'fixed' }}>
        {BRAND_COLOR_PRESETS.map((preset) => (
          <Dropdown.Item
            key={preset.hex}
            className='d-flex align-items-center zocks-gap-0.5'
            active={preset.hex.toLowerCase() === value.toLowerCase()}
            onClick={() => {
              onChange(preset.hex)
              setShow(false)
            }}
          >
            <Swatch hex={preset.hex} />
            <span className='paragraph-small'>
              {preset.name} ({preset.hex.toUpperCase()})
            </span>
          </Dropdown.Item>
        ))}
        <Dropdown.Divider />
        <div className='d-flex flex-column zocks-gap-0.25 px-3 py-1'>
          <label className='paragraph-small zocks-text-gray-700' htmlFor='brand-color-custom-hex'>
            Custom hex
          </label>
          <InputGroup size='sm'>
            <InputGroup.Text>#</InputGroup.Text>
            <Form.Control
              id='brand-color-custom-hex'
              value={customHex}
              maxLength={6}
              placeholder='Enter value'
              aria-label='Custom hex color'
              onChange={(event) => setCustomHex(event.currentTarget.value.replace(/[^0-9a-fA-F]/g, ''))}
              onBlur={commitCustomHex}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  commitCustomHex()
                  setShow(false)
                }
              }}
            />
            <InputGroup.Text>
              <Swatch hex={isCustomHexValid ? `#${customHex}` : 'transparent'} />
            </InputGroup.Text>
          </InputGroup>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}
